import { notifyWarning } from "./dialogs";
import { now } from "./base-values";
import { tokenStorage } from "./token-storage";

export interface SessionSettings {
    idleTimeoutMinutes: number;
    authenticationTimeoutMinutes: number;
    tokenRefreshIntervalMinutes: number;
    maxUploadBytes?: number;
}

/**
 * Web 空闲超时：仅在未勾选“记住我”时启用。
 * 用户点击 / 键盘 / 滚轮（移动端触摸）算操作；token 刷新、消息轮询等不算。
 */
class IdleTimeoutService {
    private timer = 0;
    private idleMs = 15 * 60 * 1000;
    private started = false;
    private readonly lastActivityKey = "idle.lastActivity";
    private readonly onActivity = () => this.touch();
    private readonly onVisibility = () => {
        if (document.visibilityState === "visible") {
            this.checkExpired();
        }
    };

    applyConfig(idleTimeoutMinutes: number) {
        this.idleMs = Math.max(0, idleTimeoutMinutes) * 60 * 1000;
        if (this.started) {
            this.schedule();
        }
    }

    /**
     * 根据当前登录态启停：有记住我则不启空闲超时。
     */
    syncForCurrentSession(onIdle: () => void) {
        this.onIdle = onIdle;

        if (this.idleMs <= 0 || tokenStorage.longTimeToken?.remember) {
            this.stop();
            return;
        }

        if (!tokenStorage.token) {
            this.stop();
            return;
        }

        this.start();
    }

    private onIdle: () => void = () => { };

    private start() {
        if (this.started) {
            this.touch();
            return;
        }

        this.started = true;
        this.bindEvents();
        this.touch();
    }

    stop() {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = 0;
        }

        if (!this.started) {
            return;
        }

        this.started = false;
        this.unbindEvents();
        localStorage.removeItem(this.lastActivityKey);
    }

    touch() {
        if (!this.started) {
            return;
        }

        localStorage.setItem(this.lastActivityKey, now().getTime().toString());
        this.schedule();
    }

    private checkExpired() {
        if (!this.started) {
            return;
        }

        const raw = localStorage.getItem(this.lastActivityKey);
        if (!raw) {
            this.touch();
            return;
        }

        const last = parseInt(raw, 10);
        if (now().getTime() - last >= this.idleMs) {
            this.fireIdle();
        } else {
            this.schedule();
        }
    }

    private schedule() {
        if (this.timer) {
            clearTimeout(this.timer);
        }

        const raw = localStorage.getItem(this.lastActivityKey);
        const last = raw ? parseInt(raw, 10) : now().getTime();
        const remain = Math.max(0, this.idleMs - (now().getTime() - last));
        this.timer = window.setTimeout(() => this.fireIdle(), remain);
    }

    private fireIdle() {
        this.stop();
        notifyWarning("因长时间未操作，已自动退出登录");
        this.onIdle();
    }

    private bindEvents() {
        if ("ontouchstart" in window) {
            document.addEventListener("touchstart", this.onActivity, { passive: true, capture: true });
        } else {
            document.addEventListener("click", this.onActivity, true);
            document.addEventListener("keydown", this.onActivity, true);
            document.addEventListener("wheel", this.onActivity, { passive: true, capture: true });
        }

        document.addEventListener("visibilitychange", this.onVisibility);
    }

    private unbindEvents() {
        document.removeEventListener("touchstart", this.onActivity, true);
        document.removeEventListener("click", this.onActivity, true);
        document.removeEventListener("keydown", this.onActivity, true);
        document.removeEventListener("wheel", this.onActivity, true);
        document.removeEventListener("visibilitychange", this.onVisibility);
    }
}

export const idleTimeout = new IdleTimeoutService();
